import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { ModalComponentProp, useModal } from 'react-native-modalfy';

import {
  Button,
  Separator,
  PaymentForm,
  ButtonsLayout,
  ButtonStyleEnum,
} from '@components';
import { usePaymentMethod } from '@hooks';
import { ToastService } from '@services';
import { parseApiError } from '@utils';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import type { ModalStackParamsType } from '@modals';
import { ConfirmPaymentTypeEnum } from '@constants';

type ModalPropsType = ModalComponentProp<
  ModalStackParamsType,
  void,
  'AddCreditCardModal'
>;

export const AddCreditCardForm = observer(() => {
  const { t } = useTranslation();
  const viewer = useViewerModel();
  const paymentMethod = usePaymentMethod();
  const modal = useModal<ModalPropsType>();
  const [isAdding, setIsAdding] = useState(false);
  const [isPaymentFormComplete, setIsPaymentFormComplete] =
    useState(false);

  async function add() {
    try {
      setIsAdding(true);

      await paymentMethod.submit();

      const setupPaymentMethodRes =
        await viewer.paymentMethodList.setupPaymentMethod.run();

      if (!setupPaymentMethodRes) {
        throw new Error();
      }

      const setupIntent = await paymentMethod.confirmSetup({
        clientSecret: setupPaymentMethodRes.clientSecret,
        queryParams: {
          type: ConfirmPaymentTypeEnum.Method.toString(),
        },
      });

      if (!setupIntent) {
        throw new Error();
      }

      await viewer.paymentMethodList.confirmPaymentMethod.run({
        setupIntentId: setupIntent.id,
      });

      setIsAdding(false);
      modal.closeModal();
    } catch (error) {
      setIsAdding(false);
      ToastService.showError(parseApiError(error).message);
    }
  }

  function cancelAction() {
    modal.closeModal();
  }

  return (
    <View>
      <PaymentForm onChangeStatus={setIsPaymentFormComplete} />
      <Separator />
      <ButtonsLayout>
        <Button
          onPress={add}
          isLoading={isAdding}
          disabled={!isPaymentFormComplete}
        >
          {t('creditCards.add')}
        </Button>

        <Button
          onPress={cancelAction}
          buttonStyle={ButtonStyleEnum.Outline}
        >
          {t('creditCards.cancel')}
        </Button>
      </ButtonsLayout>
    </View>
  );
});
